import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { workKindLabel } from "@/lib/workKind";
import { plainExcerpt } from "@/lib/markdownPlain";
import { API_BASE_URL } from "@/lib/apiBase";

const LinkedParentDialog = ({
  open,
  parentId,
  orgId,
  accessToken,
  onClose,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !parentId || !orgId || !accessToken) {
      setData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/posts/${parentId}/linked-parent`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: { org_id: orgId },
          }
        );
        if (!cancelled) setData(response.data);
      } catch (err) {
        console.error("Failed to load linked parent:", err);
        if (!cancelled) {
          const status = err.response?.status;
          setError(
            status === 404
              ? "Parent details aren’t available on this workspace yet."
              : err.response?.data?.message || "Failed to load parent work"
          );
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, parentId, orgId, accessToken]);

  const team = data?.team;
  const teamId = team?.team_id ?? team?.teamId;
  const teamName = team?.name || (teamId ? `Team ${teamId}` : null);
  const boardHref =
    data?.can_open_on_board && orgId && teamId
      ? `/teams/${teamId}?org_id=${encodeURIComponent(orgId)}&tab=kanban&card=${parentId}`
      : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto z-[70]" overlayClassName="z-[70]">
        <DialogHeader>
          <DialogTitle>Parent work</DialogTitle>
          <DialogDescription>
            Read-only context from the parent card
            {teamName ? ` on ${teamName}` : ""}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : data ? (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="secondary">{workKindLabel(data)}</Badge>
                {teamName ? (
                  <span className="text-xs text-muted-foreground">
                    {teamName}
                  </span>
                ) : null}
              </div>
              <h3 className="text-base font-medium">
                {data.title || "Untitled"}
              </h3>
              {data.content ? (
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {plainExcerpt(data.content, 800)}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">No description.</p>
              )}
            </>
          ) : null}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {boardHref ? (
            <Button asChild variant="outline" size="sm">
              <Link to={boardHref} onClick={onClose}>
                Open on {teamName || "board"}
              </Link>
            </Button>
          ) : (
            <span />
          )}
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LinkedParentDialog;
